"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import { cva } from "class-variance-authority";
import { type ComponentPropsWithoutRef, type ElementRef, forwardRef } from "react";

import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@moment/icons";

import { cn } from "../utils";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const triggerVariants = cva(
	[
		"flex",
		"items-center",
		"gap-2",
		"w-full",

		"text-sm",

		"bg-primary",

		"select-none",

		"duration-200",

		"border",
		"border-palette-gray-7",
		"hover:border-palette-gray-8",
		"focus:border-palette-brand-8",
		"active:border-palette-brand-7",

		"outline-1",
		"-outline-offset-1",
		"focus:outline",
		"focus:outline-2",
		"focus:-outline-offset-2",

		"disabled:cursor-not-allowed",
		"disabled:opacity-50",
		"[&>span]:line-clamp-1",
	],
	{
		variants: {
			size: { md: ["h-8", "px-2", "py-2", "rounded-md"] },
			color: {
				brand: [
					"data-[placeholder]:text-tertiary",
					"placeholder:text-tertiary",

					"focus:outline-palette-brand-9",
					"focus-within:outline-palette-brand-9",

					"text-primary",

					"border-palette-gray-7",
					"hover:border-palette-gray-8",
					"focus:border-palette-brand-8",
					"active:border-palette-brand-7",

					"[&_svg]:text-palette-gray-11",
					"[&_svg]:hover:text-palette-gray-12",
					"[&_svg]:focus:text-palette-gray-12",
					"[&_svg]:active:text-palette-gray-12",
				],
				negative: [
					"data-[placeholder]:text-palette-negative-10",
					"placeholder:text-palette-negative-10",

					"focus:outline-palette-negative-9",
					"focus-within:outline-palette-negative-9",

					"text-palette-negative-11",

					"selection:bg-palette-negative-9",

					"[&_svg]:text-fixed-light-default",
					"[&_svg]:hover:text-fixed-light-hover",
					"[&_svg]:focus:text-fixed-light-focus",
					"[&_svg]:active:text-fixed-light-active",

					"border-palette-negative-7",
					"hover:border-palette-negative-8",
					"focus:border-palette-negative-8",
					"active:border-palette-negative-7",
				],
			},
			elevation: {
				raised: "shadow-sm hover:shadow",
				flat: "shadow-none",
			},
		},
		defaultVariants: {
			color: "brand",
			size: "md",
			elevation: "flat",
		},
	}
);

const SelectTrigger = forwardRef<
	ElementRef<typeof SelectPrimitive.Trigger>,
	ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
		size?: "md";
		elevation?: "flat" | "raised";
		color?: "brand" | "negative";
	}
>(({ className, children, size, elevation, color, ...props }, ref) => (
	<SelectPrimitive.Trigger
		ref={ref}
		className={
			props.asChild ? className : cn(triggerVariants({ size, elevation, color }), className)
		}
		{...props}
	>
		{props.asChild ? (
			children
		) : (
			<>
				{children}
				<SelectPrimitive.Icon asChild>
					<ChevronDownIcon className="ml-auto h-4 w-4 opacity-50" />
				</SelectPrimitive.Icon>
			</>
		)}
	</SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = forwardRef<
	ElementRef<typeof SelectPrimitive.ScrollUpButton>,
	ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.ScrollUpButton
		ref={ref}
		className={cn("flex cursor-default items-center justify-center py-1", className)}
		{...props}
	>
		<ChevronUpIcon className="h-4 w-4" />
	</SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = forwardRef<
	ElementRef<typeof SelectPrimitive.ScrollDownButton>,
	ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.ScrollDownButton
		ref={ref}
		className={cn("flex cursor-default items-center justify-center py-1", className)}
		{...props}
	>
		<ChevronDownIcon className="h-4 w-4" />
	</SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = forwardRef<
	ElementRef<typeof SelectPrimitive.Content>,
	ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & { withPortal?: boolean }
>(({ className, children, position = "popper", withPortal = true, ...props }, ref) => {
	const content = (
		<SelectPrimitive.Content
			ref={ref}
			className={cn(
				"relative",
				"z-popover",
				"max-h-96",
				"min-w-[8rem]",
				"overflow-hidden",
				"rounded-md",
				"border",
				"bg-primary-raised",
				"shadow-md",
				"data-[state=open]:animate-in",
				"data-[state=closed]:animate-out",
				"data-[state=closed]:fade-out-0",
				"data-[state=open]:fade-in-0",
				"data-[state=closed]:zoom-out-95",
				"data-[state=open]:zoom-in-95",
				"data-[side=bottom]:slide-in-from-top-2",
				"data-[side=left]:slide-in-from-right-2",
				"data-[side=right]:slide-in-from-left-2",
				"data-[side=top]:slide-in-from-bottom-2",
				position === "popper" &&
					"data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
				className
			)}
			position={position}
			{...props}
		>
			<SelectScrollUpButton />
			<SelectPrimitive.Viewport
				className={cn(
					"p-1",
					position === "popper" &&
						"h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
				)}
			>
				{children}
			</SelectPrimitive.Viewport>
			<SelectScrollDownButton />
		</SelectPrimitive.Content>
	);

	if (!withPortal) {
		return content;
	}

	return <SelectPrimitive.Portal>{content}</SelectPrimitive.Portal>;
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = forwardRef<
	ElementRef<typeof SelectPrimitive.Label>,
	ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Label
		ref={ref}
		className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
		{...props}
	/>
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

export const CLEAR_SELECT_ITEM_VALUE = "66b80ea2-f1fd-4ae2-83af-846ce49435ac";

const SelectItem = forwardRef<
	ElementRef<typeof SelectPrimitive.Item>,
	ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { suffix?: React.ReactNode }
>(({ className, children, suffix, ...props }, ref) => (
	<SelectPrimitive.Item
		ref={ref}
		className={cn(
			"relative",
			"flex",
			"w-full",
			"cursor-default",
			"select-none",
			"items-center",
			"rounded",
			"py-1.5",
			"pl-8",
			"pr-2",
			"text-sm",
			"outline-none",
			"focus:bg-palette-brand-9",
			"focus:text-fixed-light-default",
			"data-[disabled]:pointer-events-none",
			"disabled:pointer-events-none",
			"data-[disabled]:opacity-50",
			"disabled:opacity-50",
			"disabled:cursor-not-allowed",
			className
		)}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<SelectPrimitive.ItemIndicator>
				<CheckIcon className="h-4 w-4" />
			</SelectPrimitive.ItemIndicator>
		</span>

		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
		{suffix}
	</SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = forwardRef<
	ElementRef<typeof SelectPrimitive.Separator>,
	ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
	<SelectPrimitive.Separator
		ref={ref}
		className={cn("-mx-1 my-1 border-t", className)}
		{...props}
	/>
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
};
